import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import {
  PolicyLayout,
  PolicyNotice,
  PolicySection,
} from "./Terms";
import "../css/returns.scss";

const returnSections = [
  { id: "return-overview", label: "At a glance" },
  { id: "return-eligibility", label: "What can be returned" },
  { id: "freshness-issues", label: "Perishable quality issues" },
  { id: "start-return", label: "How to start a return" },
  { id: "refund-timing", label: "Refund timing" },
  { id: "gifts-exchanges", label: "Gifts and exchanges" },
  { id: "return-exceptions", label: "Exceptions" },
  { id: "return-contact", label: "Contact us" },
];

function Returns() {
  return (
    <PolicyLayout
      eyebrow="Customer care · Returns"
      intro="Food is personal—and freshness matters. Here is exactly what to do when an item is not right, arrives damaged, or qualifies for a standard return."
      modifier="returns"
      sections={returnSections}
      title="Returns & quality promise"
    >
      <PolicyNotice title="Before starting a return">
        <p>
          Contact us before sending anything back. Perishable goods require prompt
          review and generally cannot be returned through ordinary post.
        </p>
      </PolicyNotice>

      <PolicySection id="return-overview" title="1. At a glance">
        <div className="policy-page__cards returns-policy__summary">
          <article>
            <span>14 days</span>
            <h3>Non-perishable returns</h3>
            <p>Contact us within 14 calendar days of delivery.</p>
          </article>
          <article>
            <span>24 hours</span>
            <h3>Freshness concerns</h3>
            <p>Report damage, warmth, or spoilage within 24 hours of arrival.</p>
          </article>
          <article>
            <span>Original state</span>
            <h3>Keep it unopened</h3>
            <p>Eligible goods must be unused and in original packaging.</p>
          </article>
          <article>
            <span>Proof helps</span>
            <h3>Save the packaging</h3>
            <p>Photos of the item, label, and shipping box help us resolve issues.</p>
          </article>
        </div>
        <p>
          We handle each concern fairly and with food safety in mind. Eligibility
          depends on the item type, condition, delivery circumstances, and timing
          described below.
        </p>
      </PolicySection>

      <PolicySection id="return-eligibility" title="2. What can be returned">
        <p>
          An unopened, unused, non-perishable item may be returned when you contact
          us within 14 calendar days after delivery. It must include its original
          labels, accessories, seals, and packaging and be in a condition suitable
          for resale.
        </p>
        <div className="policy-page__table-wrap">
          <table className="policy-page__table">
            <thead>
              <tr>
                <th scope="col">Item or situation</th>
                <th scope="col">Standard eligibility</th>
                <th scope="col">Contact window</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Unopened tools, boards, linens, or shelf-stable goods</td>
                <td>Returnable in original condition, subject to review</td>
                <td>Within 14 days of delivery</td>
              </tr>
              <tr>
                <td>Cheese, meat, prepared food, baked goods, or other perishables</td>
                <td>Not returnable for preference or change of mind</td>
                <td>Quality concerns within 24 hours</td>
              </tr>
              <tr>
                <td>Wrong, missing, damaged, or unsafe item</td>
                <td>Eligible for review and an appropriate remedy</td>
                <td>Within 24 hours when possible</td>
              </tr>
              <tr>
                <td>Gift card, personalized item, or final-sale item</td>
                <td>Normally non-returnable unless defective or required by law</td>
                <td>Contact us promptly</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Do not send an item back without authorization. Food and temperature-
          sensitive goods cannot safely re-enter inventory, and an unauthorized
          parcel may be refused.
        </p>
      </PolicySection>

      <PolicySection id="freshness-issues" title="3. Perishable quality issues">
        <p>
          Perishable food is not eligible for a conventional return. If it arrives
          damaged, unacceptably warm, spoiled, or different from what you ordered,
          contact customer care within 24 hours of delivery. We may offer a
          replacement, store credit, or refund after reviewing the circumstances.
        </p>
        <p>Please keep and photograph:</p>
        <ul>
          <li>the affected item and its product label or date code;</li>
          <li>the outside shipping carton and any visible impact damage;</li>
          <li>the thermal liner and cold packs, when included; and</li>
          <li>the packing slip or order reference.</li>
        </ul>
        <PolicyNotice tone="warning" title="Food safety first">
          <p>
            Do not taste food that appears unsafe, has an unusual odor, has damaged
            packaging, or arrived outside a safe temperature range. Keep it away
            from other foods while you contact us.
          </p>
        </PolicyNotice>
      </PolicySection>

      <PolicySection id="start-return" title="4. How to start a return">
        <ol className="policy-page__steps">
          <li>
            <div>
              <strong>Contact customer care</strong>
              <p>
                Use the <Link to="/contact">contact form</Link>, email
                {` ${siteConfig.contact.email}`}.
              </p>
            </div>
          </li>
          <li>
            <div>
              <strong>Share the order details</strong>
              <p>Include the order number, item name, delivery date, and a concise description.</p>
            </div>
          </li>
          <li>
            <div>
              <strong>Add clear photos if needed</strong>
              <p>For damage, incorrect goods, or quality issues, photograph the item and packaging.</p>
            </div>
          </li>
          <li>
            <div>
              <strong>Wait for instructions</strong>
              <p>We will confirm eligibility and provide a return authorization or another remedy.</p>
            </div>
          </li>
          <li>
            <div>
              <strong>Pack approved returns securely</strong>
              <p>Use the original packaging when possible and keep the carrier receipt until resolved.</p>
            </div>
          </li>
        </ol>
      </PolicySection>

      <PolicySection id="refund-timing" title="5. Refund timing">
        <p>
          Approved refunds are issued to the original payment method. For a
          physical return, inspection normally takes 2–3 business days after it
          reaches us. After approval, most banks post the credit within 5–10
          business days; your bank controls the final timing.
        </p>
        <ul>
          <li>Original delivery charges are not refundable for preference-based returns.</li>
          <li>Return postage is the customer&apos;s responsibility unless we sent the wrong item or the item arrived defective.</li>
          <li>Promotional discounts are allocated across the order; a refund reflects the amount actually paid for the returned item.</li>
          <li>If a whole order is refunded, any associated earned reward or promotional benefit may be reversed.</li>
        </ul>
      </PolicySection>

      <PolicySection id="gifts-exchanges" title="6. Gifts and exchanges">
        <p>
          A gift recipient may contact us with the purchaser&apos;s name, recipient
          address, and order or packing-slip reference. To protect the purchaser&apos;s
          information, we may confirm limited order details before offering an
          eligible store credit or replacement. Refunds normally return to the
          original payment method.
        </p>
        <p>
          We do not process direct exchanges. For an eligible non-perishable item,
          complete the approved return and place a new order for the preferred item.
          This keeps inventory and delivery timing clear.
        </p>
      </PolicySection>

      <PolicySection id="return-exceptions" title="7. Exceptions">
        <p>A return or refund may be declined when:</p>
        <ul>
          <li>the request falls outside the applicable contact window;</li>
          <li>an item was opened, used, altered, or stored contrary to its label;</li>
          <li>spoilage resulted from an incorrect address or an unavailable recipient;</li>
          <li>the parcel was left unattended after a documented delivery;</li>
          <li>the item is personalized, a gift card, or clearly marked final sale; or</li>
          <li>required evidence is discarded before we can reasonably review the concern.</li>
        </ul>
        <p>
          These limits do not reduce any mandatory consumer rights that apply where
          you live. We may make a reasonable exception at our discretion without
          creating an ongoing obligation to do so.
        </p>
      </PolicySection>

      <PolicySection id="return-contact" title="8. Contact us">
        <dl className="policy-page__facts">
          <div>
            <dt>Email</dt>
            <dd><a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a></dd>
          </div>
          <div>
            <dt>Hours</dt>
            <dd>{siteConfig.contact.hours}</dd>
          </div>
          <div>
            <dt>Related policy</dt>
            <dd><Link to="/shipping">Shipping and delivery</Link></dd>
          </div>
        </dl>
      </PolicySection>
    </PolicyLayout>
  );
}

export default Returns;
